import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ComposedLegalDocument,
  isOrganisational,
  LEGAL_DOCUMENT_TYPE_LABELS,
  LEGAL_PLATFORM_ICONS,
  LEGAL_PLATFORM_LABELS,
  LEGAL_PLATFORMS,
  LegalDocument,
  LegalDocumentType,
  LegalPlatform,
  legalPartsOf,
  legalTypesOf
} from '../../models/legal-document';
import { LegalDocumentService } from '../../../services/legal-document.service';

/**
 * The legal documents of each app, picked with the platform switch - one tab per document, every
 * version listed. The products' documents are composed: the General platform holds the part they
 * all share, each product its addendum, and "Preview" shows the two together as users read them.
 * The DPA is accepted per organisation, so its counts are organisations.
 * A change is always a
 * new version: "New version" starts a draft from the current text, and publishing it (on the detail
 * page, where the rendered text is in view) makes every user accept it again.
 */
@Component({
  selector: 'app-legal-documents',
  templateUrl: './legal-documents.component.html',
  styleUrls: ['./legal-documents.component.scss']
})
export class LegalDocumentsComponent implements OnInit {

  readonly typeLabels = LEGAL_DOCUMENT_TYPE_LABELS;
  readonly platforms = LEGAL_PLATFORMS;
  readonly platformLabels = LEGAL_PLATFORM_LABELS;
  readonly platformIcons = LEGAL_PLATFORM_ICONS;

  platform: LegalPlatform = LEGAL_PLATFORMS[0];
  selectedIndex = 0;
  documents: LegalDocument[] = [];
  loading = true;
  refreshing = false;
  error: string | null = null;
  starting = false;

  preview: ComposedLegalDocument | null = null;
  previewing = false;

  formOpen = false;
  editing: LegalDocument | null = null;

  constructor(private legalService: LegalDocumentService, private router: Router, private route: ActivatedRoute) {}

  ngOnInit(): void {
    // Kept in the URL so "back" from a version's page lands on the same platform.
    const fromUrl = this.route.snapshot.queryParamMap.get('platform') as LegalPlatform | null;
    if (fromUrl && this.platforms.includes(fromUrl)) {
      this.platform = fromUrl;
    }
    this.load();
  }

  get types(): LegalDocumentType[] {
    return legalTypesOf(this.platform);
  }

  /** Whether users read this tab's document together with another part. */
  get composed(): boolean {
    return legalPartsOf(this.platform, this.selectedType).length > 1;
  }

  get organisational(): boolean {
    return isOrganisational(this.selectedType);
  }

  selectPlatform(platform: LegalPlatform): void {
    this.platform = platform;
    this.selectedIndex = 0;
    this.preview = null;
    this.router.navigate([], { queryParams: { platform }, replaceUrl: true });
  }

  countOf(platform: LegalPlatform): number {
    return this.documents.filter(d => d.platform === platform).length;
  }

  get selectedType(): LegalDocumentType {
    return this.types[this.selectedIndex];
  }

  documentsOf(type: LegalDocumentType): LegalDocument[] {
    return this.documents.filter(d => d.platform === this.platform && d.type === type);
  }

  currentOf(type: LegalDocumentType): LegalDocument | undefined {
    return this.documents.find(d => d.platform === this.platform && d.type === type && d.current);
  }

  draftOf(type: LegalDocumentType): LegalDocument | undefined {
    return this.documents.find(d => d.platform === this.platform && d.type === type && d.status === 'DRAFT');
  }

  onTabChange(index: number): void {
    this.selectedIndex = index;
    this.preview = null;
  }

  togglePreview(): void {
    if (this.preview) {
      this.preview = null;
      return;
    }
    this.previewing = true;
    this.legalService.preview(this.platform, this.selectedType).subscribe({
      next: (document) => {
        this.previewing = false;
        this.preview = document;
      },
      error: () => {
        this.previewing = false;
        this.error = 'Could not load the preview.';
      }
    });
  }

  load(): void {
    this.refreshing = !this.loading;
    this.error = null;
    this.legalService.list().subscribe({
      next: (response) => {
        this.documents = response.data ?? [];
        this.loading = false;
        this.refreshing = false;
      },
      error: () => {
        this.error = 'Could not load the legal documents.';
        this.loading = false;
        this.refreshing = false;
      }
    });
  }

  /**
   * An open draft is continued rather than a second one started. With a current version the draft
   * starts as a copy of it; the very first version starts empty.
   */
  newVersion(): void {
    const type = this.selectedType;
    const draft = this.draftOf(type);
    if (draft) {
      this.openEdit(draft);
      return;
    }
    const current = this.currentOf(type);
    if (!current) {
      this.editing = null;
      this.formOpen = true;
      return;
    }
    this.starting = true;
    this.legalService.copy(current.id).subscribe({
      next: (copy) => {
        this.starting = false;
        this.documents = [copy, ...this.documents];
        this.openEdit(copy);
      },
      error: (err) => {
        this.starting = false;
        this.error = err?.error?.exceptionMessage || err?.error?.message || 'Could not start a new version.';
      }
    });
  }

  openEdit(document: LegalDocument, event?: Event): void {
    event?.stopPropagation();
    this.editing = document;
    this.formOpen = true;
  }

  onSaved(document: LegalDocument): void {
    this.formOpen = false;
    this.editing = null;
    // Saving a draft leads straight to its page, where it is reviewed rendered and published.
    this.router.navigate(['/legal-documents', document.id]);
  }

  open(document: LegalDocument): void {
    this.router.navigate(['/legal-documents', document.id]);
  }

  statusLabel(document: LegalDocument): string {
    if (document.status === 'DRAFT') {
      return 'Draft';
    }
    return document.current ? 'Current' : 'Superseded';
  }

  statusClass(document: LegalDocument): string {
    if (document.status === 'DRAFT') {
      return 'catalog-status status-draft';
    }
    return document.current ? 'catalog-status status-published' : 'catalog-status status-archived';
  }

  trackById(_index: number, document: LegalDocument): number {
    return document.id;
  }
}
